/**
 * @fileoverview Syndicator Showdown — Phase 5: Lightweight presence tracking
 * @module utils/presence
 *
 * Writes `users/{uid}.presence` so the server-side email-fallback trigger
 * (functions/src/notifications/notificationPreferences.js#isUserActive) can
 * decide whether a user is "active" right now. Deliberately lightweight —
 * this is a best-effort signal, not a real-time presence system.
 *
 * Used from web/src/App.jsx's SessionGuard (heartbeat + route change +
 * visibility/focus) and web/src/components/shell/AppShell.jsx (explicit
 * "Log out" button, best-effort isActive:false).
 */

import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../services/firebase';

// How often to refresh presence while the tab is visible and a heartbeat
// interval fires. Kept separate from App.jsx's 5-minute `lastActive` write
// (which powers the admin "currently logged in" stat) — this one exists
// specifically to keep the 5-minute inactivity window (below) accurate.
export const PRESENCE_HEARTBEAT_MS = 60 * 1000;

// Must match functions/src/notifications/notificationPreferences.js
// INACTIVE_AFTER_MS — the client and server definitions of "active" must
// never drift apart, since the server makes the actual email-fallback call.
export const PRESENCE_INACTIVE_AFTER_MS = 5 * 60 * 1000;

/**
 * Best-effort presence write — never throws. Safe to call frequently; a
 * failure (offline, doc not yet created, or a write racing sign-out) is
 * silently swallowed since presence is a soft signal, not critical state.
 *
 * @param {string|null|undefined} uid
 * @param {{ isActive: boolean, currentPath?: string }} opts
 */
export async function writePresence(uid, { isActive, currentPath } = {}) {
  if (!uid) return;
  try {
    await updateDoc(doc(db, 'users', uid), {
      presence: {
        isActive:    !!isActive,
        lastSeenAt:  serverTimestamp(),
        currentPath: currentPath || null,
        updatedAt:   serverTimestamp(),
      },
    });
  } catch {
    // Best-effort — see file header.
  }
}
